import React from 'react';
import { Gavel, Briefcase, Users, Home as HomeIcon, ChevronRight, ShieldCheck } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import T from '../T';
import { useSiteContent } from '../../hooks/useSiteContent';
import { useLocalizedText } from '../../hooks/useLocalizedText';
import type { LocalizedText } from '../../utils/siteContent';

import BlogSection from './BlogSection';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { content } = useSiteContent();
  const heroSubtitle = useLocalizedText(content.landing.heroSubtitle);
  const aboutTitle = useLocalizedText(content.landing.aboutTitle);
  const aboutIntro = useLocalizedText(content.landing.aboutIntro);
  const crossBorderTitle = useLocalizedText(content.landing.crossBorderTitle);
  const teamTitle = useLocalizedText(content.landing.teamTitle);
  const teamIntro = useLocalizedText(content.landing.teamIntro);
  const visibleTeamProfiles = content.team.filter((profile) => profile.visible);
  const serviceIcons: Record<string, React.ReactNode> = {
    'civil-litigation': <Gavel size={40} color="var(--primary-color)" />,
    'business-law': <Briefcase size={40} color="var(--primary-color)" />,
    'marriage-family-law': <Users size={40} color="var(--primary-color)" />,
    'real-estate-law': <HomeIcon size={40} color="var(--primary-color)" />,
    'estates-wealth-succession': <ShieldCheck size={40} color="var(--primary-color)" />,
  };

  const futuristicBodyTextColor = '#334155';

  return (
    <div className="landing-page">

      <section className="hero-section">
        <video 
          key="futuristic"
          className="hero-video" 
          autoPlay 
          muted 
          playsInline
          poster="/images/toronto-skyline-517x345.jpg"
          onCanPlay={(e) => {
            e.currentTarget.playbackRate = 0.5;
          }}
        >
          <source src="/law_video.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div className="container hero-content" style={{ position: 'absolute', top: '40px', left: '50%', transform: 'translateX(-50%)', width: '100%' }}>
          <h1 className="slide-down notranslate" translate="no" style={{ 
            fontSize: '4.5rem', 
            paddingBottom: '10px',
            marginBottom: '0.75rem', 
            fontWeight: 'bold', 
            letterSpacing: '-0.02em',
            color: '#E0E0E0',
            textShadow: '0 0 15px rgba(0, 212, 255, 0.6)'
          }}>
            Y. Liu Law Firm
          </h1>
          <p style={{ 
            fontSize: '1.4rem', 
            maxWidth: '800px', 
            margin: '0 auto 2.5rem', 
            opacity: '0.9', 
            lineHeight: '1.4',
            color: 'white',
            fontWeight: '600'
          }}>
            {heroSubtitle}
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
            <Link to="/intake" className="btn btn-secondary" style={{ color: 'var(--white)' }}><T>Book a Consultation</T></Link>
            <a href="#services" className="btn" style={{ border: '2px solid var(--primary-color)', color: 'var(--primary-color)' }}><T>Our Services</T></a>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="section-padding">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 style={{ fontSize: '2.5rem', color: 'var(--primary-color)', marginBottom: '1rem' }}><T>Expert Legal Services</T></h2>
            <div style={{ width: '60px', height: '4px', backgroundColor: 'var(--secondary-color)', margin: '0 auto' }}></div>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '2rem'
          }}>
            {content.services.map((service) => (
              <div 
                key={service.id}
                onClick={() => navigate(`/services/${service.id}`)}
                className="card"
                style={{
                  padding: '2.5rem',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: 'var(--shadow)',
                  transition: 'var(--transition)',
                  cursor: 'pointer',
                  border: '1px solid #f0f0f0'
                }}
                onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateY(-10px)')}
                onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
              >
                <div style={{ marginBottom: '1.5rem' }}>{serviceIcons[service.id] || <Gavel size={40} color="var(--primary-color)" />}</div>
                <h3 style={{ marginBottom: '1rem', color: 'var(--primary-color)' }}>{service.cardTitle}</h3>
                <p style={{ color: futuristicBodyTextColor, marginBottom: '1.5rem', whiteSpace: 'pre-line' }}>{service.cardDescription}</p>
                <Link to={`/services/${service.id}`} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', color: 'var(--secondary-color)', fontWeight: 'bold', textDecoration: 'none' }}>
                  <T>Learn More</T> <ChevronRight size={16} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <BlogSection />

      {/* About Section */}
      <section style={{ backgroundColor: '#ffffff', padding: '80px 0' }}>
        <div className="container" style={{ marginBottom: '3rem' }}>
          <h2 style={{ fontSize: '2.5rem', color: 'var(--primary-color)', marginBottom: '1rem', textAlign: 'center' }}><T>About Us</T></h2>
          <p style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center', fontSize: '1.1rem', color: futuristicBodyTextColor, whiteSpace: 'pre-line' }}>{aboutIntro}</p>
        </div>

        <div className="container" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '4rem', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: '2.5rem', color: 'var(--primary-color)', marginBottom: '1.5rem' }}>{crossBorderTitle}</h2>
            <p style={{ marginBottom: '1.5rem', fontSize: '1.1rem', color: futuristicBodyTextColor }}>
              <LocalizedParagraph value={content.landing.crossBorderParagraphs[0]} />
            </p>
            <p style={{ marginBottom: '2rem', fontSize: '1.1rem', color: futuristicBodyTextColor }}>
              <LocalizedParagraph value={content.landing.crossBorderParagraphs[1]} />
            </p>
            <Link to="/login" className="btn btn-primary"><T>Join Our Client Community</T></Link>
          </div>
          <div style={{ borderRadius: '8px', overflow: 'hidden', boxShadow: 'var(--shadow)', border: '1px solid var(--primary-color)', width: '517px', maxWidth: '100%' }}>
            <img 
              src={content.landing.aboutImage.src || '/images/toronto-skyline-517x345.jpg'} 
              alt={useLocalizedText(content.landing.aboutImage.alt)} 
              loading="lazy"
              decoding="async"
              width={517}
              height={345}
              style={{ width: '100%', height: '345px', objectFit: 'cover', display: 'block' }}
            />
            <p style={{ margin: '0.75rem 1rem 1rem', fontSize: '0.92rem', color: futuristicBodyTextColor, whiteSpace: 'pre-line' }}>{useLocalizedText(content.landing.aboutImage.caption)}</p>
          </div>
        </div>

        <div id="our-team" className="container" style={{ marginTop: '4rem' }}>
          <h3 style={{ fontSize: '2rem', color: 'var(--primary-color)', marginBottom: '1.25rem', textAlign: 'center' }}>{teamTitle}</h3>
          <p style={{ maxWidth: '860px', margin: '0 auto 2.5rem', textAlign: 'center', fontSize: '1.05rem', color: futuristicBodyTextColor, whiteSpace: 'pre-line' }}>{teamIntro}</p>
          <div style={{ display: 'grid', gap: '2rem' }}>
            {visibleTeamProfiles.map((profile) => (
              <TeamProfileCard key={profile.id} profile={profile} bodyColor={futuristicBodyTextColor} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

const TeamProfileCard: React.FC<{
  profile: {
    id: string;
    name: LocalizedText;
    image: string;
    imageAlt: LocalizedText;
    imageCaption: LocalizedText;
    summary: LocalizedText;
  };
  bodyColor: string;
}> = ({ profile, bodyColor }) => {
  const name = useLocalizedText(profile.name);
  const imageAlt = useLocalizedText(profile.imageAlt);
  const summary = useLocalizedText(profile.summary);
  const imageCaption = useLocalizedText(profile.imageCaption);

  return (
    <article
      className="card team-profile-card"
      style={{
        gap: '2rem',
        padding: '2rem',
        backgroundColor: 'white',
        borderRadius: '18px',
        boxShadow: '0 18px 45px rgba(15, 23, 42, 0.12)',
        border: '1px solid rgba(0, 212, 255, 0.2)'
      }}
    >
      <h3 className="team-profile-name" style={{ fontSize: '2rem', marginBottom: '0', color: 'var(--primary-color)' }}>{name}</h3>
      <div
        className="team-profile-media"
        style={{
          width: '100%',
          maxWidth: '420px',
          maxHeight: '420px',
          margin: '0 auto',
          padding: '1rem',
          backgroundColor: '#f4f7fb',
          borderRadius: '24px',
          boxShadow: '0 18px 35px rgba(15, 23, 42, 0.14)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column'
        }}
      >
        {profile.image ? (
          <img
            className="team-profile-image"
            src={profile.image}
            alt={imageAlt}
            loading="lazy"
            decoding="async"
            style={{
              width: '100%',
              maxWidth: '400px',
              maxHeight: '400px',
              aspectRatio: '1 / 1',
              objectFit: 'cover',
              display: 'block',
              borderRadius: '18px',
              border: '6px solid #ffffff'
            }}
          />
        ) : null}
        {imageCaption ? <p className="team-profile-caption" style={{ marginTop: '0.75rem', fontSize: '0.9rem', color: bodyColor, whiteSpace: 'pre-line', textAlign: 'center' }}>{imageCaption}</p> : null}
      </div>
      <p className="team-profile-summary" style={{ fontSize: '1.06rem', lineHeight: '1.85', color: bodyColor, whiteSpace: 'pre-line', margin: 0 }}>{summary}</p>
    </article>
  );
};

const LocalizedParagraph: React.FC<{ value: LocalizedText }> = ({ value }) => {
  const text = useLocalizedText(value);
  return <>{text}</>;
};

export default LandingPage;
